import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SubmissionsTable from "@/components/SubmissionsTable";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function PricingSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Pricing settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const fields = [
    ["base_car_am", "Car Recovery Base AM (RM, <10KM)", "Day rate 08:00 - 19:59"],
    ["base_car_pm", "Car Recovery Base PM (RM, <10KM)", "Night rate 20:00 - 07:59"],
    ["base_moto_am", "Motorcycle Recovery Base AM (RM, <10KM)", "Day rate 08:00 - 19:59"],
    ["base_moto_pm", "Motorcycle Recovery Base PM (RM, <10KM)", "Night rate 20:00 - 07:59"],
    ["base_km", "Base Distance (KM)", "Included in base price"],
    ["extra_rate", "Extra Rate (RM per KM)", "Charged beyond base distance"],
    ["runner_commission_car", "Runner Commission — Car (RM per job)", "Fixed flat amount per car towing job"],
    ["driver_commission_car", "Driver Commission — Car (RM per job)", "Fixed flat amount per car towing job"],
    ["runner_commission_moto", "Runner Commission — Motorcycle (RM per job)", "Fixed flat amount per motorcycle towing job"],
    ["driver_commission_moto", "Driver Commission — Motorcycle (RM per job)", "Fixed flat amount per motorcycle towing job"],
  ];

  return (
    <div className="max-w-2xl space-y-6 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="pricing-settings">
      <div>
        <h3 className="font-display text-xl font-bold tracking-tight">Pricing & Commission Settings</h3>
        <p className="mt-1 text-sm text-muted-foreground">Changes apply to new submissions only.</p>
      </div>
      {fields.map(([key, label, hint]) => (
        <div key={key} className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</Label>
          <Input data-testid={`setting-${key}`} type="number" step="0.01" value={s[key]}
            onChange={(e) => setS({ ...s, [key]: parseFloat(e.target.value) || 0 })}
            className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
      ))}
      <Button data-testid="settings-save-button" onClick={save} disabled={busy}
        className="rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
        {busy ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}

export default function AdminPanel({ staff }) {
  const [subs, setSubs] = useState([]);

  const refresh = useCallback(() => {
    api.get("/submissions").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const stats = [
    ["Total Jobs", subs.length],
    ["Pending", subs.filter((s) => s.status === "pending").length],
    ["Active", subs.filter((s) => ["assigned", "en_route", "arrived"].includes(s.status)).length],
    ["Completed", subs.filter((s) => s.status === "completed").length],
  ];

  return (
    <Tabs defaultValue="jobs" data-testid="admin-panel">
      <TabsList className="mb-6 rounded-sm border border-border bg-card">
        <TabsTrigger data-testid="tab-admin-jobs" value="jobs" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All Submissions</TabsTrigger>
        <TabsTrigger data-testid="tab-admin-pricing" value="pricing" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Pricing</TabsTrigger>
      </TabsList>
      <TabsContent value="jobs" className="space-y-6">
        <div className="grid grid-cols-2 gap-px border border-border/60 bg-border/40 lg:grid-cols-4">
          {stats.map(([label, val], i) => (
            <div key={label} className="bg-card p-6" data-testid={`admin-stat-${i}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className="mt-2 font-display text-3xl font-black text-foreground">{val}</p>
            </div>
          ))}
        </div>
        <SubmissionsTable submissions={subs} onRefresh={refresh} canEdit canUpdateStatus staff={staff} />
      </TabsContent>
      <TabsContent value="pricing">
        <PricingSettings />
      </TabsContent>
    </Tabs>
  );
}
